import { updateProfile } from '../containers/ProfileSettingsPage/ProfileSettingsPage.duck';

class LastActiveTracker {
  constructor() {
    this.dispatch = null;
    this.currentUser = null;
    this.updateInterval = null;
    this.inactivityTimeout = null;
    this.isActive = true;

    // Update every 5 minutes when active
    this.ACTIVE_UPDATE_INTERVAL = 5 * 60 * 1000;
    // Consider inactive after 5 minutes of no interaction
    this.INACTIVITY_THRESHOLD = 5 * 60 * 1000;
  }

  // Initialize the tracker
  init(dispatch, currentUser) {
    this.dispatch = dispatch;
    this.currentUser = currentUser;

    if (!currentUser) return;

    // Set up event listeners
    this.setupEventListeners();

    // Update immediately on init
    this.updateLastActive();

    // Start periodic updates
    this.startPeriodicUpdates();
  }

  // Set up all event listeners
  setupEventListeners() {
    // Track user activity
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach(event => {
      document.addEventListener(event, this.handleActivity.bind(this), { passive: true });
    });

    // Track page visibility changes (tab switch)
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));

    // Track before page unload (close/refresh)
    window.addEventListener('beforeunload', this.handleBeforeUnload.bind(this));

    // Track when user comes back online
    window.addEventListener('online', this.handleOnline.bind(this));
  }

  // Handle user activity
  handleActivity() {
    if (!this.isActive) {
      this.isActive = true;
      this.updateLastActive();
      this.startPeriodicUpdates();
    }

    // Reset inactivity timeout
    this.resetInactivityTimeout();
  }

  // Reset inactivity timeout
  resetInactivityTimeout() {
    if (this.inactivityTimeout) {
      clearTimeout(this.inactivityTimeout);
    }

    this.inactivityTimeout = setTimeout(() => {
      this.isActive = false;
      this.stopPeriodicUpdates();
      this.updateLastActive(); // One final update when going inactive
    }, this.INACTIVITY_THRESHOLD);
  }

  // Handle visibility change (tab switch)
  handleVisibilityChange() {
    if (document.hidden) {
      // Tab is hidden - update last active
      this.updateLastActive();
      this.stopPeriodicUpdates();
    } else {
      // Tab is visible again - resume tracking
      this.isActive = true;
      this.updateLastActive();
      this.startPeriodicUpdates();
      this.resetInactivityTimeout();
    }
  }

  // Handle before unload (close/refresh)
  handleBeforeUnload() {
    this.updateLastActive();
    this.cleanup();
  }

  // Handle coming back online
  handleOnline() {
    this.updateLastActive();
  }

  // Start periodic updates
  startPeriodicUpdates() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }

    this.updateInterval = setInterval(() => {
      if (this.isActive && !document.hidden) {
        this.updateLastActive();
      }
    }, this.ACTIVE_UPDATE_INTERVAL);
  }

  // Stop periodic updates
  stopPeriodicUpdates() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
  }

  // Update last active timestamp
  updateLastActive() {
    if (!this.dispatch || !this.currentUser) return;

    const lastActive = new Date().toISOString();

    this.dispatch(
      updateProfile({
        publicData: {
          lastActive: lastActive,
        },
      })
    ).catch(error => {
      console.error('Failed to update lastActive:', error);
    });
  }

  // Update current user (call this when user changes)
  updateUser(currentUser) {
    this.currentUser = currentUser;
    if (currentUser) {
      this.updateLastActive();
      if (!this.updateInterval) {
        this.startPeriodicUpdates();
      }
    } else {
      this.cleanup();
    }
  }

  // Cleanup when user logs out
  cleanup() {
    this.stopPeriodicUpdates();

    if (this.inactivityTimeout) {
      clearTimeout(this.inactivityTimeout);
      this.inactivityTimeout = null;
    }

    // Remove event listeners
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach(event => {
      document.removeEventListener(event, this.handleActivity.bind(this));
    });

    document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    window.removeEventListener('beforeunload', this.handleBeforeUnload.bind(this));
    window.removeEventListener('online', this.handleOnline.bind(this));

    this.currentUser = null;
    this.isActive = true;
  }
}

// Create a singleton instance
const lastActiveTracker = new LastActiveTracker();

export default lastActiveTracker;
